import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Calendar as CalIcon, GanttChart, LineChart as TimelineIcon, AlertOctagon, TrendingUp } from "lucide-react";

const DAY_MS = 86400000;
const PALETTE = ["#f97316","#0ea5e9","#10b981","#8b5cf6","#f59e0b","#ec4899","#14b8a6","#eab308"];

const isoDate = (d) => d.toISOString().slice(0,10);
const parseDate = (s) => s ? new Date(s + "T00:00:00") : null;

function daysBetween(a, b) { return Math.round((b - a) / DAY_MS); }

export default function ScheduleTab({ pid, project }) {
  const [data, setData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [mode, setMode] = useState("gantt");

  const load = () => api.get(`/projects/${pid}/schedule`).then((r) => setData(r.data));
  useEffect(() => { load(); api.get("/employees").then(r => setEmployees(r.data)); /* eslint-disable-next-line */ }, [pid]);

  const empName = (id) => employees.find(e => e.id === id)?.name || "Unassigned";

  if (!data) return <div className="p-6 text-sm text-muted-foreground">Loading schedule…</div>;

  const projectStart = parseDate(data.project_start) || parseDate(project?.start_date);
  const projectEnd   = parseDate(data.project_end)   || parseDate(project?.end_date);

  return (
    <div className="space-y-4" data-testid="schedule-tab">
      {/* Header */}
      <div className="rounded-2xl bg-card border border-border/60 p-5">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Start</div><div className="font-display text-lg mt-0.5">{data.project_start || "—"}</div></div>
          <div><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">End</div><div className="font-display text-lg mt-0.5">{data.project_end || "—"}</div></div>
          <div><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Auto Progress</div><div className="font-display text-lg mt-0.5" data-testid="auto-progress">{data.auto_progress}%</div></div>
          <div><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Works</div><div className="font-display text-lg mt-0.5">{data.works.length}</div></div>
          <div><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-1"><AlertOctagon className="w-3 h-3 text-rose-500" /> Delayed</div><div className="font-display text-lg mt-0.5 text-rose-600" data-testid="delayed-count">{data.delayed_works.length}</div></div>
        </div>
      </div>

      {/* View toggle */}
      <div className="rounded-2xl bg-card border border-border/60 p-1 inline-flex" role="tablist">
        {[
          { k: "gantt", l: "Gantt", i: GanttChart },
          { k: "timeline", l: "Timeline", i: TimelineIcon },
          { k: "calendar", l: "Calendar", i: CalIcon },
        ].map(v => (
          <button key={v.k} onClick={() => setMode(v.k)} data-testid={`schedule-mode-${v.k}`}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm ${mode===v.k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            <v.i className="w-3.5 h-3.5" /> {v.l}
          </button>
        ))}
      </div>

      {mode === "gantt" && <Gantt data={data} projectStart={projectStart} projectEnd={projectEnd} empName={empName} />}
      {mode === "timeline" && <Timeline data={data} empName={empName} />}
      {mode === "calendar" && <MonthCalendar data={data} empName={empName} />}

      {/* Resource utilisation */}
      <div className="rounded-2xl bg-card border border-border/60 p-5" data-testid="resource-utilisation">
        <h3 className="font-display text-base mb-4">Resource Allocation</h3>
        {data.resource_utilisation.length === 0 ? (
          <div className="text-sm text-muted-foreground">No works assigned yet — add works and assign employees.</div>
        ) : (
          <div className="space-y-3">
            {data.resource_utilisation.map(r => {
              const load = Math.min(100, r.total * 20);
              return (
                <div key={r.assignee_id} className="grid grid-cols-6 items-center gap-3">
                  <div className="col-span-2 text-sm font-medium truncate">{empName(r.assignee_id)}</div>
                  <div className="col-span-3">
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${load}%` }} />
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">{r.done}/{r.total} done · {r.in_progress} active</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Gantt (custom SVG) ----
function Gantt({ data, projectStart, projectEnd, empName }) {
  const works = data.works.filter(w => w.start_date || w.due_date);
  const items = [
    ...data.phases.map(p => ({ ...p, kind: "phase", title: p.name })),
    ...works.map(w => ({ ...w, kind: "work" })),
  ];
  const dates = items.flatMap(i => [parseDate(i.start_date), parseDate(i.due_date || i.end_date)]).filter(Boolean);
  if (projectStart) dates.push(projectStart); if (projectEnd) dates.push(projectEnd);
  if (!dates.length) return <div className="rounded-2xl bg-card border border-border/60 p-8 text-center text-sm text-muted-foreground">Add start/end dates to phases or works to see the Gantt chart.</div>;
  const min = new Date(Math.min(...dates.map(d => d.getTime())));
  const max = new Date(Math.max(...dates.map(d => d.getTime())));
  const totalDays = Math.max(1, daysBetween(min, max));
  const rowH = 36; const pxPerDay = Math.max(6, Math.min(28, 900/Math.max(1,totalDays)));
  const width = totalDays * pxPerDay + 220;
  const depIds = new Set(data.dependencies.map(d => d.to_id));

  const today = new Date();
  const todayX = 220 + Math.max(0, daysBetween(min, today)) * pxPerDay;

  return (
    <div className="rounded-2xl bg-card border border-border/60 overflow-hidden" data-testid="gantt-container">
      <div className="p-4 border-b border-border/50 text-xs text-muted-foreground">
        {items.length} bars · {isoDate(min)} → {isoDate(max)} · {totalDays} days · Today marker in orange
      </div>
      <div className="overflow-x-auto">
        <svg width={Math.max(width, 900)} height={Math.max(60, items.length * rowH + 60)} className="text-xs">
          {/* Header ticks (weekly) */}
          {Array.from({ length: Math.ceil(totalDays/7)+1 }, (_,i) => i*7).map((d, i) => (
            <g key={i}>
              <line x1={220 + d*pxPerDay} x2={220 + d*pxPerDay} y1={20} y2={40 + items.length*rowH} stroke="hsl(var(--border))" strokeOpacity={0.4} />
              <text x={220 + d*pxPerDay + 2} y={16} fill="hsl(var(--muted-foreground))" fontSize="10">{isoDate(new Date(min.getTime() + d*DAY_MS)).slice(5)}</text>
            </g>
          ))}
          {/* Today line */}
          <line x1={todayX} x2={todayX} y1={20} y2={40 + items.length*rowH} stroke="#f97316" strokeWidth={1.5} strokeDasharray="4 3" />
          <text x={todayX + 4} y={32} fill="#f97316" fontSize="10">Today</text>

          {items.map((it, idx) => {
            const start = parseDate(it.start_date) || parseDate(it.due_date || it.end_date) || min;
            const end   = parseDate(it.due_date || it.end_date) || parseDate(it.start_date) || start;
            const x = 220 + daysBetween(min, start) * pxPerDay;
            const w = Math.max(6, daysBetween(start, end) * pxPerDay);
            const y = 40 + idx * rowH;
            const isDelayed = data.delayed_works.includes(it.id);
            const color = it.kind === "phase" ? "#8b5cf6" : (isDelayed ? "#ef4444" : PALETTE[idx % PALETTE.length]);
            const progressW = w * ((it.progress||0)/100);
            const label = it.title + (it.assigned_to ? ` · ${empName(it.assigned_to)}` : "");
            return (
              <g key={it.id} data-testid={`gantt-bar-${it.id}`}>
                <text x={210} y={y+18} textAnchor="end" fill="hsl(var(--foreground))" fontSize="11">{it.title?.slice(0,26) || "—"}</text>
                <rect x={x} y={y+6} width={w} height={20} fill={color} opacity={0.25} rx={4} />
                <rect x={x} y={y+6} width={progressW} height={20} fill={color} rx={4} />
                <text x={x+6} y={y+20} fill="white" fontSize="10" style={{ pointerEvents: "none" }}>{(it.progress||0)}%</text>
                {isDelayed && <circle cx={x-6} cy={y+16} r={4} fill="#ef4444" />}
                {depIds.has(it.id) && <text x={x-2} y={y+4} fill="#0ea5e9" fontSize="9">↩</text>}
                <title>{label} · {it.start_date || "—"} → {it.due_date || it.end_date || "—"} · {it.progress||0}%</title>
              </g>
            );
          })}
          {items.length === 0 && <text x={20} y={80} fill="hsl(var(--muted-foreground))">No items with dates.</text>}
        </svg>
      </div>
    </div>
  );
}

// ---- Timeline (horizontal ordered list) ----
function Timeline({ data, empName }) {
  const events = [
    ...data.milestones.map(m => ({ type: "milestone", date: m.target_date, title: m.title, status: m.status, id: m.id })),
    ...data.works.filter(w => w.due_date).map(w => ({ type: "work", date: w.due_date, title: w.title, status: w.status, id: w.id, assignee: w.assigned_to, delayed: data.delayed_works.includes(w.id) })),
    ...data.phases.map(p => ({ type: "phase", date: p.end_date, title: p.name, status: p.status, id: p.id })),
  ].filter(e => e.date).sort((a,b) => a.date.localeCompare(b.date));

  return (
    <div className="rounded-2xl bg-card border border-border/60 p-6" data-testid="timeline-container">
      {events.length === 0 ? <div className="text-sm text-muted-foreground text-center py-6">No dated events yet.</div> : (
        <div className="relative border-l-2 border-border/60 ml-3 space-y-4">
          {events.map(e => (
            <div key={e.type+e.id} data-testid={`timeline-event-${e.id}`} className="relative pl-6">
              <div className={`absolute left-0 w-3 h-3 rounded-full -translate-x-1/2 mt-1.5 ${e.delayed ? "bg-rose-500" : e.type==="milestone" ? "bg-purple-500" : e.type==="phase" ? "bg-blue-500" : "bg-emerald-500"}`} />
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{e.date}</span>
                <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-muted">{e.type}</span>
                {e.delayed && <span className="text-[10px] text-rose-600 font-semibold">DELAYED</span>}
              </div>
              <div className="text-sm font-medium mt-0.5">{e.title}</div>
              {e.assignee && <div className="text-[11px] text-muted-foreground">Assigned: {empName(e.assignee)}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Calendar (month grid) ----
function MonthCalendar({ data, empName }) {
  const [cursor, setCursor] = useState(new Date());
  const y = cursor.getFullYear(), m = cursor.getMonth();
  const first = new Date(y, m, 1);
  const firstDow = first.getDay();
  const daysInMonth = new Date(y, m+1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const eventsFor = (date) => {
    if (!date) return [];
    const iso = isoDate(date);
    const list = [];
    data.works.forEach(w => { if (w.due_date === iso) list.push({ id: w.id, kind: "work", title: w.title, delayed: data.delayed_works.includes(w.id) }); });
    data.milestones.forEach(x => { if (x.target_date === iso) list.push({ id: x.id, kind: "milestone", title: x.title }); });
    data.phases.forEach(p => { if (p.end_date === iso) list.push({ id: p.id, kind: "phase", title: p.name }); });
    return list;
  };

  const shift = (delta) => { const d = new Date(cursor); d.setMonth(d.getMonth()+delta); setCursor(d); };
  const monthLabel = cursor.toLocaleDateString("en-GB",{month:"long",year:"numeric"});

  return (
    <div className="rounded-2xl bg-card border border-border/60 p-4" data-testid="calendar-container">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => shift(-1)} className="px-3 py-1 text-sm rounded hover:bg-muted">←</button>
        <div className="font-display text-lg">{monthLabel}</div>
        <button onClick={() => shift(1)} className="px-3 py-1 text-sm rounded hover:bg-muted">→</button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => <div key={d} className="p-1 text-center">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          const evts = eventsFor(d);
          const isToday = d && isoDate(d) === isoDate(new Date());
          return (
            <div key={i} className={`min-h-[70px] rounded-md border ${d ? "border-border/50" : "border-transparent"} p-1.5 ${isToday ? "bg-primary/10 border-primary/40" : "bg-card"}`}>
              {d && <div className="text-[10px] font-medium text-muted-foreground">{d.getDate()}</div>}
              <div className="space-y-0.5 mt-0.5">
                {evts.slice(0,3).map(e => (
                  <div key={e.kind+e.id} className={`text-[10px] px-1 py-0.5 rounded truncate ${e.delayed ? "bg-rose-100 text-rose-700" : e.kind==="milestone" ? "bg-purple-100 text-purple-700" : e.kind==="phase" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>
                    {e.title}
                  </div>
                ))}
                {evts.length > 3 && <div className="text-[9px] text-muted-foreground">+{evts.length-3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
